import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp, MessageCircle } from 'lucide-react';
import { LanguageSelector } from './LanguageSelector';
import { ShareButtons } from './ShareButtons';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
export const Footer = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    language
  } = useLanguage();
  const t = translations[language];
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <footer className="bg-black text-white relative overflow-hidden" data-id="element-419">
      <div className="absolute inset-0 bg-gradient-to-t from-amber-900/10 to-transparent opacity-20" data-id="element-420"></div>
      <div className="container mx-auto px-4 py-20 relative z-10" data-id="element-421">
        {/* Language Selector */}
        <div className="flex justify-center mb-12" data-id="element-422">
          <LanguageSelector data-id="element-423" />
        </div>
        {/* Share Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-2xl mx-auto mb-20 border border-amber-400/30 p-8 bg-black/40" data-id="element-424">
          <ShareButtons layout="horizontal" title="Share This Cultural Experience" data-id="element-425" />
        </motion.div>
        {/* FAQ Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-4xl mx-auto mb-20" data-id="element-426">
          <h4 className="text-center font-serif text-3xl text-amber-400 mb-12 tracking-wider uppercase" data-id="element-427">
            {t.faqTitle}
          </h4>
          <div className="space-y-4" data-id="element-428">
            {t.faqQuestions.map((faq, index) => <div key={index} className="border border-amber-400/30 bg-black/40 overflow-hidden" data-id="element-429">
                <button onClick={() => toggleFaq(index)} className="flex justify-between items-center w-full p-6 text-left" data-id="element-430">
                  <span className="text-white font-light pr-4" data-id="element-431">
                    {faq.question}
                  </span>
                  {openFaq === index ? <ChevronUp className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-432" /> : <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-433" />}
                </button>
                {openFaq === index && <div className="px-6 pb-6 text-white/70 font-light leading-relaxed border-t border-amber-400/20 pt-4" data-id="element-434">
                    {faq.answer}
                  </div>}
              </div>)}
          </div>
        </motion.div>
        {/* Footer Links */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16 border-t border-amber-400/30 pt-16" data-id="element-435">
          {/* Brand */}
          <div className="md:col-span-2" data-id="element-436">
            <div className="flex items-center mb-6" data-id="element-437">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/a1thpmaUHZUisJ3HgEhAjA/oldkhanda.png" alt="Khanda Symbol" className="w-10 h-10 mr-3 object-contain" data-id="element-438" />
              <h3 className="font-serif text-2xl text-amber-400" data-id="element-439">
                {t.footerTitle}
              </h3>
            </div>
            <p className="text-white/60 mb-6 max-w-md font-light leading-relaxed" data-id="element-440">
              {t.footerDescription}
            </p>
          </div>
          {/* Quick Links */}
          <div data-id="element-441">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-442">
              {t.quickLinks}
            </h4>
            <ul className="space-y-3" data-id="element-443">
              {t.links.map((link, index) => <li key={index} data-id="element-444">
                  <a href={link.href} className="text-white/60 hover:text-amber-400 transition-colors text-sm font-light" data-id="element-445">
                    {link.text}
                  </a>
                </li>)}
            </ul>
          </div>
          {/* Contact */}
          <div data-id="element-446">
            <h4 className="text-amber-400 font-light text-sm mb-4 tracking-[0.3em] uppercase" data-id="element-447">
              {t.contactUs}
            </h4>
            <a href="https://chat.whatsapp.com/Joakmqmpdd3A0riGDdBEEb?mode=wwc" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-500 text-black font-medium py-3 px-6 transition-colors text-sm" data-id="element-448">
              <MessageCircle className="w-4 h-4" data-id="element-449" />
              {t.subscribe}
            </a>
          </div>
        </div>
        {/* Logo & Copyright */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center border-t border-amber-400/30 pt-12" data-id="element-450">
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="flex justify-center mb-8" data-id="element-451">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqXvi2nxLJFgni6jP9pU4X/image.png" alt="Sikh Kingdoms Logo" className="w-32 h-32 object-cover rounded-full opacity-80" data-id="element-452" />
          </motion.div>
          <p className="text-white/40 text-xs tracking-wider uppercase font-light" data-id="element-453">
            {t.copyright}
            <br data-id="element-454" />
            {t.allRightsReserved}
          </p>
        </motion.div>
      </div>
    </footer>;
};